import * as React from "react"
import { Dialog as SheetPrimitive } from "radix-ui"
import { XIcon } from "lucide-react"
import { cn } from "src/utils/cn"

function Sheet({ ...props }) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({ ...props }) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({ ...props }) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({ ...props }) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({ className, ...props }) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        // Fade timing/keyframes authored in application.css (keyed off data-slot
        // + data-state) so Radix awaits the fade-out on close, in sync with the
        // panel slide.
        "fixed inset-0 z-50 bg-black/50",
        className
      )}
      {...props}
    />
  )
}

// Floating, inset drawer that matches the shadcn "base" Drawer look:
// an 8px margin on every side, fully rounded corners, a full border + shadow,
// and a slide-in animation eased with the shadcn drawer curve.
//
// Positioning + width live here in Tailwind; the actual slide (a GPU-composited
// translate3d, the transition curve, and durations) is authored as plain CSS in
// app/assets/tailwind/application.css, keyed off data-slot="sheet-content" and
// data-side. Tailwind v4 rewrites arbitrary `translate3d(...)` down to 2D
// `translate()`, which drops hardware acceleration and the crisp feel — so the
// motion is written by hand there to mirror shadcn exactly.
const SHEET_SIDES = {
  right:  "inset-y-2 right-2 w-[90%]",
  left:   "inset-y-2 left-2 w-[90%]",
  top:    "inset-x-2 top-2",
  bottom: "inset-x-2 bottom-2",
}

function SheetContent({ side = "right", className, children, ...props }) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          // Slide (translate3d, curve, 450ms open / 400ms close) is authored in
          // application.css keyed off data-slot + data-side, so it stays a crisp
          // GPU-composited transform. Only look + positioning are set here.
          "fixed z-50 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl",
          SHEET_SIDES[side],
          side === "right" && "sm:max-w-[480px]",
          side === "left"  && "sm:max-w-[480px]",
          className
        )}
        {...props}
      >
        {children}
        <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:pointer-events-none">
          <XIcon className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }) {
  return (
    <div className={cn("flex flex-col space-y-1.5 px-6 pt-6 pb-4 border-b border-slate-100", className)} {...props} />
  )
}

function SheetFooter({ className, ...props }) {
  return (
    <div className={cn("flex flex-row justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50", className)} {...props} />
  )
}

function SheetTitle({ className, ...props }) {
  return (
    <SheetPrimitive.Title className={cn("text-base font-semibold text-slate-900", className)} {...props} />
  )
}

function SheetDescription({ className, ...props }) {
  return (
    <SheetPrimitive.Description className={cn("text-sm text-slate-500", className)} {...props} />
  )
}

export { Sheet, SheetPortal, SheetOverlay, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription }
